/**
 * Desktop (`lg` and up) layout of the Compliance Certifications section: a heading row with the
 * intro, then two rows of three items (title, text with an inline PDF link, icon). Each item is
 * written out by hand, in content order; there is no loop over the content keys.
 *
 * Each PDF opens in a new tab from the static-assets host (`NEXT_PUBLIC_URI_STATIC`), in the
 * visitor's language folder. The California transparency policy is linked from
 * `policies-terms-conditions/`, the others from `compliance-certifications/` (see the header of
 * `./ComplianceCertificationsContent.tsx`). Pinned by
 * `src/unit-test/WhyRyerson/ComplianceCertificationsDesktop.spec.tsx`.
 */
import React from "react";
import { useTheme } from "@ryerson/frontend.theme";
import { ContentSection, Flex } from "@ryerson/frontend.layout";
import { Typography } from "@ryerson/frontend.typography";
import { Link } from "@ryerson/frontend.navigation";
import {
	ComplianceCertificationsContent,
	ComplianceCertificationsText,
} from "./ComplianceCertificationsContent";
import { useApplication } from "@ryerson/frontend.application";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

/**
 * Empty, absolutely positioned jump target for the `#ryerson-canada-labor-report` deep link.
 * `scroll-margin-top` stops a native hash jump 120px above it; `Shared/AllSections/AllSections.tsx`
 * also scrolls to it (110px offset) when the page loads with that hash.
 */
const SectionAnchor = styled("div")`
	position: absolute;
	scroll-margin-top: 120px;
`;

const SectionHeader = styled(Flex)`
	${(props: any) => {
		const { borderColor } = props;
		return css`
			border-bottom: 1px solid ${borderColor};
		`;
	}};
	padding-bottom: 47px;
	padding-top: 85px;
`;

const Title = styled(Typography)`
	width: 390px;
`;

const Subtitle = styled(Typography)`
	width: 561px;
`;

const SectionRow = styled(Flex)`
	margin-top: 35px;
	max-height: 333px;
`;

const SectionItem = styled(Flex)`
	width: 359px;
	padding-right: 20px;
`;

const SectionItemText = styled(Flex)`
	padding-bottom: 15px;
`;

const SectionItemTitle = styled(Typography)`
	margin-bottom: 25px;
	margin-top: 0;
`;

const IconContainer = styled(Flex)`
	max-height: 83px;
`;

const Icon = styled("img")`
	width: 83px;
`;

/** Renders the section in the visitor's language. It takes no props. */
const ComplianceCertificationsDesktop: React.FC = ({ ...props }) => {
	const { theme } = useTheme();
	const { lighterGray } = theme.colors.primary;

	const {
		localization: { language },
	} = useApplication();

	const {
		main,
		conflictMinerals,
		caliTransparencyAct,
		caliProp65Act,
		rohs,
		itar,
		ryersonCanadaLaborReport,
	}: ComplianceCertificationsText = ComplianceCertificationsContent[language];

	return (
		<ContentSection type="primary">
			<SectionHeader alignItems="center" {...{ borderColor: lighterGray }}>
				<Title variant="h2">{main.title}</Title>
				<Subtitle>{main.text}</Subtitle>
			</SectionHeader>
			<SectionRow justifyContent="space-between">
				<SectionItem direction="column" justifyContent="space-between">
					<SectionItemText direction="column">
						<SectionItemTitle weight="bold" variant="h5">
							{conflictMinerals.title}
						</SectionItemTitle>
						<Typography>
							{conflictMinerals.text}
							<Link
								target="_blank"
								next={false}
								to={`${process.env.NEXT_PUBLIC_URI_STATIC}/static-assets/documents/pdfs/${language}/compliance-certifications/${conflictMinerals.pdfLink}`}
							>
								{conflictMinerals.downloadText}
							</Link>
						</Typography>
					</SectionItemText>
					<IconContainer>
						<Icon src={conflictMinerals.icon} alt={conflictMinerals.title} />
					</IconContainer>
				</SectionItem>
				<SectionItem direction="column" justifyContent="space-between">
					<SectionItemText direction="column">
						<SectionItemTitle weight="bold" variant="h5">
							{caliTransparencyAct.title}
						</SectionItemTitle>
						<Typography>
							{caliTransparencyAct.text}
							{/* `policies-terms-conditions/`: the only folder web-static-assets
							    has `CaliforniaTransparencyPolicy.pdf` in.
							    `compliance-certifications/` holds only an older copy
							    named `CaliforniaTransparency.pdf`. */}
							<Link
								target="_blank"
								next={false}
								to={`${process.env.NEXT_PUBLIC_URI_STATIC}/static-assets/documents/pdfs/${language}/policies-terms-conditions/${caliTransparencyAct.pdfLink}`}
							>
								{caliTransparencyAct.downloadText}
							</Link>
						</Typography>
					</SectionItemText>
					<IconContainer>
						<Icon src={caliTransparencyAct.icon} alt={caliTransparencyAct.title} />
					</IconContainer>
				</SectionItem>
				<SectionItem direction="column" justifyContent="space-between">
					<SectionItemText direction="column">
						<SectionItemTitle weight="bold" variant="h5">
							{caliProp65Act.title}
						</SectionItemTitle>
						<Typography>
							{caliProp65Act.text}
							<Link
								target="_blank"
								next={false}
								to={`${process.env.NEXT_PUBLIC_URI_STATIC}/static-assets/documents/pdfs/${language}/compliance-certifications/${caliProp65Act.pdfLink}`}
							>
								{caliProp65Act.downloadText}
							</Link>
						</Typography>
					</SectionItemText>
					<IconContainer>
						<Icon src={caliProp65Act.icon} alt={caliProp65Act.title} />
					</IconContainer>
				</SectionItem>
			</SectionRow>
			<SectionRow justifyContent="space-between">
				<SectionItem direction="column" justifyContent="space-between">
					<SectionItemText direction="column">
						<SectionItemTitle weight="bold" variant="h5">
							{rohs.title}
						</SectionItemTitle>
						<Typography>
							{rohs.text}
							<Link
								target="_blank"
								next={false}
								to={`${process.env.NEXT_PUBLIC_URI_STATIC}/static-assets/documents/pdfs/${language}/compliance-certifications/${rohs.pdfLink}`}
							>
								{rohs.downloadText}
							</Link>
						</Typography>
					</SectionItemText>
					<IconContainer>
						<Icon src={rohs.icon} alt={rohs.title} />
					</IconContainer>
				</SectionItem>
				<SectionItem direction="column" justifyContent="space-between">
					<SectionItemText direction="column">
						<SectionItemTitle weight="bold" variant="h5">
							{itar.title}
						</SectionItemTitle>
						<Typography>
							{itar.text}
							<Link
								target="_blank"
								next={false}
								to={`${process.env.NEXT_PUBLIC_URI_STATIC}/static-assets/documents/pdfs/${language}/compliance-certifications/${itar.pdfLink}`}
							>
								{itar.downloadText}
							</Link>
						</Typography>
					</SectionItemText>
					<IconContainer>
						<Icon src={itar.icon} alt={itar.title} />
					</IconContainer>
				</SectionItem>
				<SectionItem direction="column" justifyContent="space-between">
					{/* Fixed id, the same in every language and in the mobile layout; both specs
					    look it up. Nothing in this repository links to it. */}
					<SectionAnchor id="ryerson-canada-labor-report" />
					<SectionItemText direction="column">
						<SectionItemTitle weight="bold" variant="h5">
							{ryersonCanadaLaborReport.title}
						</SectionItemTitle>
						<Typography>
							{ryersonCanadaLaborReport.text}
							<Link
								target="_blank"
								next={false}
								to={`${process.env.NEXT_PUBLIC_URI_STATIC}/static-assets/documents/pdfs/${language}/compliance-certifications/${ryersonCanadaLaborReport.pdfLink}`}
							>
								{ryersonCanadaLaborReport.downloadText}
							</Link>
							<Link
								target="_blank"
								next={false}
								to={`${process.env.NEXT_PUBLIC_URI_STATIC}/static-assets/documents/pdfs/${language}/compliance-certifications/${ryersonCanadaLaborReport.pdfLink2}`}
							>
								{ryersonCanadaLaborReport.downloadText2}
							</Link>
						</Typography>
					</SectionItemText>
					<IconContainer>
						<Icon
							src={ryersonCanadaLaborReport.icon}
							alt={ryersonCanadaLaborReport.title}
						/>
					</IconContainer>
				</SectionItem>
			</SectionRow>
		</ContentSection>
	);
};

export default ComplianceCertificationsDesktop;
